import { AxiosResponse } from "axios";
import {
  IApiRequest,
  IApiToastMessageConfig,
  ICancelInspectionRequest,
  IGreenTagRequest,
  IInspection,
  IInspectionAttempt,
} from "@miplatform/types";
import { API_VERSION, TASK_SERVICE, X_API_VERSION } from "../../../constants";
import { apiOptions, apiRequest } from "../../../utils";

const API_INSPECTION = `${TASK_SERVICE}/inspections`;
const API_INSPECTION_ATTEMPT = `${TASK_SERVICE}/inspection-attempts`;

/**
 * Inspection service for managing inspection-related API operations.
 * Provides methods for CRUD operations and inspection-specific actions.
 * @namespace inspection
 */
export const inspection = {
  getAll: async ({
    filters,
    sort,
    params,
    signal,
  }: IApiRequest<IInspection>): Promise<AxiosResponse<IInspection[]>> => {
    const options = await apiOptions({
      url: API_INSPECTION,
      method: "get",
      signal,
      params,
      filters,
      sort,
    });
    return apiRequest(options);
  },
  getAllAttempts: async ({
    filters,
    sort,
    params,
    signal,
  }: IApiRequest<IInspectionAttempt>): Promise<
    AxiosResponse<IInspectionAttempt[]>
  > => {
    const options = await apiOptions({
      url: API_INSPECTION_ATTEMPT,
      method: "get",
      signal,
      params,
      filters,
      sort,
    });
    return apiRequest(options);
  },
  getById: async ({ id }: { id: string }) => {
    const options = await apiOptions({
      url: `${API_INSPECTION}/${id}`,
      method: "get",
    });
    return apiRequest(options);
  },
  getAttemptById: async ({ id }: { id: string }) => {
    const options = await apiOptions({
      url: `${API_INSPECTION_ATTEMPT}/${id}`,
      method: "get",
    });
    return apiRequest(options);
  },
  create: async ({
    data,
    toastMessageConfig,
  }: {
    data: any; // TODO - replace with payload type
    toastMessageConfig?: IApiToastMessageConfig;
  }) => {
    const options = await apiOptions({
      url: API_INSPECTION,
      method: "post",
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  schedule: async ({
    attemptId,
    scheduledDate,
    toastMessageConfig,
  }: {
    attemptId: string;
    scheduledDate: string;
    toastMessageConfig?: IApiToastMessageConfig;
  }) => {
    const options = await apiOptions({
      url: `${API_INSPECTION_ATTEMPT}/${attemptId}/schedule`,
      method: "post",
      data: { scheduledDate },
    });
    return apiRequest(options, toastMessageConfig);
  },
  cancel: async ({
    id,
    data,
    toastMessageConfig,
  }: {
    id: string;
    data: ICancelInspectionRequest;
    toastMessageConfig?: IApiToastMessageConfig;
  }) => {
    const options = await apiOptions({
      url: `${API_INSPECTION_ATTEMPT}/${id}/cancel`,
      method: "post",
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  pass: async ({
    id,
    data,
    toastMessageConfig,
  }: {
    id: string;
    data: IGreenTagRequest;
    toastMessageConfig?: IApiToastMessageConfig;
  }) => {
    const options = await apiOptions({
      url: `${API_INSPECTION_ATTEMPT}/${id}/pass`,
      method: "post",
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  fail: async ({
    id,
    data,
    toastMessageConfig = {},
  }: {
    id: string;
    data: { inspectedDate: string; reportedDate: string };
    toastMessageConfig?: IApiToastMessageConfig;
  }) => {
    const options = await apiOptions({
      url: `${API_INSPECTION_ATTEMPT}/${id}/fail`,
      method: "post",
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  update: async ({
    id,
    data,
    toastMessageConfig,
  }: {
    id: string;
    data: Partial<IInspection>;
    toastMessageConfig?: IApiToastMessageConfig;
  }) => {
    const options = await apiOptions({
      url: `${API_INSPECTION}/${id}`,
      method: "patch",
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  delete: async ({
    id,
    modifiedOn,
    toastMessageConfig,
  }: {
    id: string;
    modifiedOn: string;
    toastMessageConfig?: IApiToastMessageConfig;
  }) => {
    const options = await apiOptions({
      url: `${API_INSPECTION}/${id}`,
      method: "delete",
      headers: { [X_API_VERSION]: API_VERSION },
      data: { modifiedOn },
    });
    return apiRequest(options, toastMessageConfig);
  },
  updateConfigById: async ({
    id,
    data,
    modifiedOn,
    toastMessageConfig,
  }: {
    id: string;
    data: Partial<IInspection>;
    modifiedOn: string;
    toastMessageConfig?: IApiToastMessageConfig;
  }) => {
    const options = await apiOptions({
      url: `${API_INSPECTION}/${id}`,
      method: "patch",
      data,
      modifiedOn,
    });
    return apiRequest(options, toastMessageConfig);
  },
};
